/**
 * @file UserProfileContext.js
 * @brief Provides the authenticated user's profile data to the React component tree.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useUnifiedAuth } from "../hooks/useUnifiedAuth";
import { createApiClient } from "../api/axios";

const UserProfileContext = createContext(null);

/**
 * Fetch the current user's profile and expose it through context.
 * @param {Object} props Component props.
 * @param {React.ReactNode} props.children Descendant elements that consume the profile context.
 * @returns {JSX.Element} Context provider wrapping the application subtree.
 */
export function UserProfileProvider({ children }) {
    const auth = useUnifiedAuth();
    const isAuthenticated = auth.isAuthenticated;
    const accessToken = auth.accessToken;
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!isAuthenticated) {
                setProfile(null);
                setLoading(false);
                return;
            }
            try {
                const api = createApiClient({
                    isAuthenticated,
                    user: accessToken ? { access_token: accessToken } : null,
                });
                const res = await api.get("userprofiles/own");
                setProfile(res.data);
            } catch (err) {
                console.error("Error fetching user profile", err);
                setProfile(null);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [accessToken, isAuthenticated]);

    return (
        <UserProfileContext.Provider value={{ profile, loading }}>
            {children}
        </UserProfileContext.Provider>
    );
}

/**
 * Read the current user profile context.
 * @returns {{profile: Object|null, loading: boolean}} The current profile payload and loading state.
 */
export function useUserProfile() {
    return useContext(UserProfileContext);
}
